import { makeObservable, observable } from 'mobx'

import { IClosure, ILocationClosureInterval } from '~/client/graph'

import ProjectDateStore, {
  areIntervalTimesIntersects,
  isAfter,
  isWithinRange,
} from '../stores/ui/ProjectDate.store'
import BaseModel from './BaseModel'
import Workflow from './Workflow'

const DEFAULT_CLOSURE_INTERVAL: ILocationClosureInterval = {
  startDate: 0,
  endDate: 0,
  isDaily: false,
}
export default class Closure extends BaseModel<IClosure> implements IClosure {
  public static fromDto(dto: IClosure) {
    const closureInterval =
      dto.closureInterval?.isDaily !== undefined
        ? dto.closureInterval
        : DEFAULT_CLOSURE_INTERVAL

    return new Closure(
      dto.id,
      dto.projectId,
      dto.locationId,
      closureInterval,
      dto.associatedPermitId,
      dto.specifiedPermitTypes,
      dto.createdAt,
      dto.updatedAt,
    )
  }

  @observable public locationId: string = null
  @observable public closureInterval: ILocationClosureInterval =
    DEFAULT_CLOSURE_INTERVAL
  @observable public associatedPermitId: string = null
  @observable public associatedPermit: Workflow = null
  @observable public specifiedPermitTypes: string[] = []

  public constructor(
    public id: string,
    public projectId: string,
    locationId: string,
    closureInterval: ILocationClosureInterval,
    associatedPermitId: string,
    specifiedPermitTypes: string[],
    createdAt: number,
    updatedAt: number,
  ) {
    super(id)

    this.locationId = locationId

    this.associatedPermitId = associatedPermitId

    this.closureInterval = closureInterval || DEFAULT_CLOSURE_INTERVAL

    this.specifiedPermitTypes = specifiedPermitTypes || []

    this.setCreatedAt(createdAt)
    this.setUpdatedAt(updatedAt)

    makeObservable(this)
  }

  public isOpenByDate(projectDateStore: ProjectDateStore, date: Date | number) {
    return !isWithinRange(
      projectDateStore.startOfDay(date),
      projectDateStore.startOfDay(this.closureInterval.startDate),
      projectDateStore.endOfDay(this.closureInterval.endDate),
    )
  }

  public isAfter(date: Date | number) {
    return isAfter(this.closureInterval.startDate, date)
  }

  public areWithinSelectedRange(
    projectDateStore: ProjectDateStore,
    startDate: Date,
    endDate: Date,
  ): boolean {
    const { isDailyIntervalIntersectsWithDates } = projectDateStore

    if (this.closureInterval.isDaily) {
      return isDailyIntervalIntersectsWithDates(
        {
          startDate: this.closureInterval.startDate,
          endDate: this.closureInterval.endDate,
          isDaily: this.closureInterval.isDaily,
        },
        startDate,
        endDate,
      )
    }

    return areIntervalTimesIntersects(
      {
        startDate: new Date(this.closureInterval.startDate),
        endDate: new Date(this.closureInterval.endDate),
      },
      {
        startDate,
        endDate,
      },
    )
  }

  public copy(): Closure {
    return new Closure(
      this.id,
      this.projectId,
      this.locationId,
      this.closureInterval,
      this.associatedPermitId,
      this.specifiedPermitTypes,
      this.createdAt,
      this.updatedAt,
    )
  }

  public toDto(): IClosure {
    return {
      id: this.id,
      projectId: this.projectId,
      locationId: this.locationId,
      closureInterval: this.closureInterval,
      createdAt: this.createdAt,
      updatedAt: this.updatedAt,
      associatedPermitId: this.associatedPermitId,
      specifiedPermitTypes: this.specifiedPermitTypes,
    }
  }
}
